/**
 * Mention Files Service
 *
 * Unified service that aggregates files from two sources for the @ mention modal:
 * 1. PPT files from backend catalog (DB-backed presentation registry)
 * 2. External files from RAG DB (uploaded/indexed files via externalFilesCache)
 */

import { authService, PYTHON_BACKEND_URL } from "@carousel-office/shared";
import { BackendClientBase } from "@carousel-office/shared/services/backendClientBase";
import type { PresentationCatalogEntry } from "./communication/backendClient";
import { externalFilesCache, ExternalFileInfo } from './externalFilesCache';
import type { CategorizedMentionData, MentionItem, MentionSection } from "@carousel-office/shared/components/chat";
import { MentionServiceBase } from "@carousel-office/shared/services/mentionServiceBase";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type MentionFileSource = 'ppt' | 'external';

export interface UnifiedMentionFile {
  id: string;
  type: 'file';
  label: string;
  value: string;
  source: MentionFileSource;
  // PPT-specific
  documentId?: string;
  slideCount?: number;
  // External-specific
  fileType?: string;
  fileIndex?: number;
}

export interface SectionedMentionFiles {
  pptFiles: UnifiedMentionFile[];
  externalFiles: UnifiedMentionFile[];
}

interface PresentationCatalogResponse {
  success: boolean;
  presentations?: PresentationCatalogEntry[];
  error?: string;
}

class PresentationCatalogClient extends BackendClientBase {
  constructor() {
    super({
      serviceName: "MentionFilesService",
      getBaseUrl: () => PYTHON_BACKEND_URL,
      getAuthHeader: () => authService.getAuthHeader(),
      defaultHeaders: { "Content-Type": "application/json" },
      maxRetries: 1,
    });
  }

  async getPresentationsCatalog(): Promise<PresentationCatalogEntry[]> {
    const payload = await this.requestJson<PresentationCatalogResponse>(
      "/presentations/catalog",
      { method: "GET" },
      { requireAuth: true }
    );

    if (!payload.success) {
      throw new Error(payload.error || "Catalog request failed");
    }

    return Array.isArray(payload.presentations) ? payload.presentations : [];
  }
}

const presentationCatalogClient = new PresentationCatalogClient();

// ═══════════════════════════════════════════════════════════════════════════
// SERVICE CLASS
// ═══════════════════════════════════════════════════════════════════════════

class MentionFilesService extends MentionServiceBase {
  private cachedFiles: SectionedMentionFiles | null = null;
  private lastFetched: number = 0;

  /**
   * Convert a backend catalog deck entry to UnifiedMentionFile
   */
  private catalogEntryToMentionFile(deck: PresentationCatalogEntry): UnifiedMentionFile {
    return {
      id: `ppt-${deck.deck_id}`,
      type: 'file',
      label: deck.title || 'Untitled Presentation',
      value: deck.deck_id,
      source: 'ppt',
      documentId: deck.deck_id,
      slideCount: deck.slide_count || 0,
    };
  }

  private toMentionItem(file: UnifiedMentionFile, category: MentionSection["category"]): MentionItem {
    return {
      id: file.id,
      type: file.type,
      label: file.label,
      displayLabel: file.label,
      value: file.value,
      category,
      source: file.source,
      fileType: file.fileType,
      metadata: {
        documentId: file.documentId,
        slideCount: file.slideCount,
        fileIndex: file.fileIndex,
      },
    };
  }

  /**
   * Convert an ExternalFileInfo to UnifiedMentionFile
   */
  private externalFileToMentionFile(file: ExternalFileInfo): UnifiedMentionFile {
    return {
      id: `external-${file.file_id}`,
      type: 'file',
      label: file.filename,
      value: file.filepath,
      source: 'external',
      fileType: file.file_type,
      fileIndex: file.file_index,
    };
  }

  /**
   * Get all files from both sources.
   * Fetches PPT files from backend catalog and external files from the RAG cache.
   */
  async getAllFiles(): Promise<SectionedMentionFiles> {
    console.log('[MentionFilesService] Getting all files...');

    try {
      // Fetch both sources in parallel
      const [pptFiles, externalFileInfos] = await Promise.all([
        this.fetchPptFiles(),
        this.fetchExternalFiles(),
      ]);

      // Convert to unified format
      const externalFiles = externalFileInfos.map(file => this.externalFileToMentionFile(file));

      const result: SectionedMentionFiles = { pptFiles, externalFiles };

      // Update cache
      this.cachedFiles = result;
      this.lastFetched = Date.now();

      console.log(`[MentionFilesService] Found ${pptFiles.length} PPT files, ${externalFiles.length} external files`);

      return result;
    } catch (error) {
      console.error('[MentionFilesService] Error fetching files:', error);
      // Return cached data if available, otherwise empty
      return this.cachedFiles || { pptFiles: [], externalFiles: [] };
    }
  }

  /**
   * Host-specific categorized mention payload used by shared ChatInput/MentionMenu.
   */
  async searchCategorized(query?: string): Promise<CategorizedMentionData> {
    const allFiles = await this.getAllFiles();
    const filtered = this.filterByQuery(allFiles, query || "");

    const presentationItems = filtered.pptFiles.map((file) =>
      this.toMentionItem(file, "presentation")
    );
    const externalItems = filtered.externalFiles.map((file) =>
      this.toMentionItem(file, "file")
    );

    const sections: MentionSection[] = [];
    if (presentationItems.length > 0) {
      sections.push({
        id: "presentations",
        title: "Presentations",
        category: "presentation",
        items: presentationItems,
        totalCount: presentationItems.length,
      });
    }
    if (externalItems.length > 0) {
      sections.push({
        id: "files",
        title: "Files",
        category: "file",
        items: externalItems,
        totalCount: externalItems.length,
      });
    }

    return { sections };
  }

  /**
   * Fetch PPT files from backend catalog
   */
  private async fetchPptFiles(): Promise<UnifiedMentionFile[]> {
    try {
      const catalog = await presentationCatalogClient.getPresentationsCatalog();
      return catalog
        .filter((deck) => !!deck.deck_id)
        .sort((a, b) => a.deck_id.localeCompare(b.deck_id))
        .map((deck) => this.catalogEntryToMentionFile(deck));
    } catch (error) {
      console.error('[MentionFilesService] Error fetching PPT files:', error);
      return [];
    }
  }

  /**
   * Fetch external files from externalFilesCache
   */
  private async fetchExternalFiles(): Promise<ExternalFileInfo[]> {
    try {
      // ensureFresh will refresh if stale, otherwise return cached
      return await externalFilesCache.ensureFresh();
    } catch (error) {
      console.error('[MentionFilesService] Error fetching external files:', error);
      return [];
    }
  }

  /**
   * Force refresh all file data
   */
  async refresh(): Promise<void> {
    console.log('[MentionFilesService] Refreshing files...');

    // Force refresh the external files cache
    await externalFilesCache.refresh();

    // Fetch all files again
    await this.getAllFiles();
  }

  /**
   * Filter files by search query (simple name-based search)
   */
  filterByQuery(files: SectionedMentionFiles, query: string): SectionedMentionFiles {
    const normalized = this.normalizeQuery(query);
    if (!normalized) {
      return files;
    }

    return {
      pptFiles: this.filterItemsByLabel(files.pptFiles, normalized),
      externalFiles: this.filterItemsByLabel(files.externalFiles, normalized),
    };
  }

  /**
   * Get the total count of all files
   */
  getTotalCount(files: SectionedMentionFiles): number {
    return files.pptFiles.length + files.externalFiles.length;
  }

  /**
   * Get a flattened list of all files (ppt first, then external)
   */
  getFlatList(files: SectionedMentionFiles): UnifiedMentionFile[] {
    return [...files.pptFiles, ...files.externalFiles];
  }

  /**
   * Get file at a specific index in the flattened list
   */
  getFileAtIndex(files: SectionedMentionFiles, index: number): UnifiedMentionFile | null {
    const flatList = this.getFlatList(files);
    return flatList[index] || null;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cachedFiles = null;
    this.lastFetched = 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export const mentionFilesService = new MentionFilesService();
